import { inject, Injectable, Injector, runInInjectionContext, Type } from '@angular/core';
import { signalStore } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { BaseEntity } from '../base-entity/base-entity';
import { BaseEntityDescriptor } from '../base-entity/base-entity.descriptor';
import { AbstractAttrDescriptor } from '../base-entity/abstact-attr.descriptor';
import { entityNameFromType } from '../base-entity/base-entity-utility';
import { BaseEntityMapper, SimpleEntityMapper } from '../base-entity.mapper';
import { BaseEntityService } from '../base-entity-service/base-entity.service';
import { BaseEntityRestService } from '../base-entity-service/base-entity-rest.service';
import { BaseEntityStore } from '../base-entity-store/base-entity.store';
import { BaseEntityTabsStore } from '../base-tabs/base-entity-tabs.store';
import { BaseEntityContainerStore } from '../base-entity-container.store';

@Injectable()
export abstract class BaseEntityFacade<Entity extends BaseEntity> {
  abstract readonly entityType: Type<Entity>;

  protected readonly endpoint?: string;
  protected readonly backendRoot?: string;

  protected readonly injector = inject(Injector);
  private _mapper?: BaseEntityMapper<Entity>;
  private _service?: BaseEntityService<Entity>;
  private _storeClass?: Type<unknown>;
  private _storeInstance?: unknown;
  private _descriptor?: BaseEntityDescriptor;

  get mapper(): BaseEntityMapper<Entity> {
    this._mapper ??= this.createMapper();
    return this._mapper;
  }

  get service(): BaseEntityService<Entity> {
    this._service ??= this.createService(this.mapper);
    return this._service;
  }

  get storeClass(): Type<unknown> {
    this._storeClass ??= this.createStoreClass();
    return this._storeClass;
  }

  get store(): unknown {
    this._storeInstance ??= runInInjectionContext(this.injector, () => inject(this.storeClass));
    return this._storeInstance;
  }

  get descriptor(): BaseEntityDescriptor {
    const descriptor = this.declaredDescriptor();
    descriptor.store ??= this.store;
    return descriptor;
  }

  /**
   * Deliberately does not go through {@link descriptor}: the store is created with a repository, and a
   * repository may need to know which entity it serves — as an embedded one does. Binding the store into the
   * descriptor only when the descriptor itself is asked for keeps that from re-entering the store getter and
   * building a second store.
   */
  get entityName(): string {
    return this.declaredDescriptor().entityName;
  }

  get attrDescriptors(): AbstractAttrDescriptor[] {
    return this.descriptor.attrDescriptors;
  }

  get entityTitle(): string {
    const title = this.descriptor.entityTitle;
    return typeof title === 'function' ? title() : title;
  }

  protected createMapper(): BaseEntityMapper<Entity> {
    return new SimpleEntityMapper<Entity>(this.entityType);
  }

  protected createService(mapper: BaseEntityMapper<Entity>): BaseEntityService<Entity> {
    return runInInjectionContext(this.injector, () => {
      if (!this.backendRoot || !this.endpoint) {
        throw new Error(`${this.constructor.name}: REST facade requires backendRoot and endpoint`);
      }
      return new BaseEntityRestService<Entity>(mapper, this.backendRoot, this.endpoint);
    });
  }

  /** The descriptor as declared, before the store is bound into it. See {@link entityName}. */
  private declaredDescriptor(): BaseEntityDescriptor {
    this._descriptor ??= this.createDescriptor();
    return this._descriptor;
  }

  protected createStoreClass(): Type<unknown> {
    return signalStore(
      { providedIn: 'root' },
      BaseEntityStore<Entity>(this.entityType, () => this.service),
      BaseEntityTabsStore(),
      BaseEntityContainerStore(),
      withDevtools(entityNameFromType(this.entityType)),
    );
  }

  protected abstract createDescriptor(): BaseEntityDescriptor;
}
